import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/modules/collector_v2/hooks/useApi";
import { PageHeader } from "@/modules/collector_v2/components/common/PageHeader";
import { LoadingState } from "@/modules/collector_v2/components/common/LoadingState";
import { ErrorState } from "@/modules/collector_v2/components/common/ErrorState";
import { NativeHandDetails } from "@/modules/collector_v2/components/profile/native-details/NativeHandDetails";
import { NativeSceneRenderer } from "@/modules/collector_v2/components/profile/native-scene/NativeSceneRenderer";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function NativeSceneTab({ handId }) {
  const [scene, setScene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function fetchScene() {
      setLoading(true);
      setErr("");
      try {
        const res = await axios.get(`${API_BASE}/api/v2/hands/${encodeURIComponent(handId)}/native-render-scene`);
        if (!cancelled) setScene(res.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message || "Error cargando scene");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchScene();
    return () => { cancelled = true; };
  }, [handId]);

  if (loading) return <LoadingState />;
  if (err) return <ErrorState message={err} />;
  if (!scene) return <p className="text-sm text-muted-foreground">Sin datos de scene graph</p>;
  return <NativeSceneRenderer scene={scene} />;
}

export default function HandDetailPage() {
  const { handId } = useParams();
  const { data, loading, error, refetch } = useApi(
    `/v2/hands/${encodeURIComponent(handId)}`,
  );

  return (
    <>
      <PageHeader
        eyebrow="Detalle de mano"
        title={handId || "Mano"}
        description="Vista nativa, acciones y datos raw de la mano."
        action={
          <Link to="/collector-v2/manos">
            <Button variant="outline" size="sm" data-testid="hand-detail-back-button">
              <ArrowLeft className="mr-1 h-4 w-4" /> Volver
            </Button>
          </Link>
        }
      />

      {loading && <LoadingState />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {!loading && !error && data && (
        <Card data-testid="hand-detail-card">
          <CardContent className="pt-6">
            <Tabs defaultValue="nativo" data-testid="hand-detail-tabs">
              <TabsList>
                <TabsTrigger value="nativo" data-testid="hand-detail-native-tab">Vista Nativa</TabsTrigger>
                <TabsTrigger value="legacy" data-testid="hand-detail-legacy-tab">Vista Legacy</TabsTrigger>
                <TabsTrigger value="raw" data-testid="hand-detail-raw-tab">Raw JSON</TabsTrigger>
              </TabsList>

              <TabsContent value="nativo" data-testid="hand-detail-native-content">
                <NativeSceneTab handId={handId} />
              </TabsContent>

              <TabsContent value="legacy" data-testid="hand-detail-legacy-content">
                <NativeHandDetails data={data} />
              </TabsContent>

              <TabsContent value="raw" data-testid="hand-detail-raw-content">
                <pre className="mt-4 max-h-[600px] overflow-auto rounded-lg bg-muted p-4 text-xs">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </>
  );
}
